import { useContext, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  HelpCircle,
  Lock,
  ShieldAlert,
  Upload,
} from "lucide-react";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import ApplyCalendar from "./ApplyCalendar";
import AttendanceUpload from "./AttendanceUpload";
import DailyAttendance from "./DailyAttendance";
import WfFreeze from "./WfFreeze";
import {
  filterMasterTabsByRole,
  getMasterTabMeta,
  resolveMasterTab,
} from "./attendanceCalendarMasterConfig";
import "../../../styles/attendanceCalendarMaster.css";
import "../../../styles/IndianCalendar.css";

const TAB_ICONS = {
  daily: Calendar,
  import: Upload,
  apply: CalendarDays,
  lock: Lock,
};

const TAB_COMPONENTS = {
  daily: DailyAttendance,
  import: AttendanceUpload,
  apply: ApplyCalendar,
  lock: WfFreeze,
};

export default function AttendanceCalendarMaster() {
  const { role } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const allowedTabs = useMemo(() => filterMasterTabsByRole(role), [role]);
  const tabParam = searchParams.get("tab");
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!allowedTabs.length) return;
    const resolved = resolveMasterTab(tabParam, allowedTabs);
    if (tabParam !== resolved) {
      const next = new URLSearchParams(searchParams);
      if (resolved === "daily") next.delete("tab");
      else next.set("tab", resolved);
      setSearchParams(next, { replace: true });
    }
  }, [tabParam, allowedTabs, searchParams, setSearchParams]);

  if (tabParam === "holidays" || tabParam === "bulk") {
    if (tabParam === "holidays") {
      return <Navigate to="/holiday-calendar" replace />;
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", "import");
    return <Navigate to={`/attendance/apply-calendar?${next.toString()}`} replace />;
  }

  const activeTab = resolveMasterTab(tabParam, allowedTabs);
  const tabMeta = getMasterTabMeta(activeTab);
  const ActiveIcon = TAB_ICONS[activeTab] || CalendarClock;
  const ActivePanel = TAB_COMPONENTS[activeTab];

  const selectTab = (tabId) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === "daily") next.delete("tab");
    else next.set("tab", tabId);
    setSearchParams(next, { replace: true });
  };

  if (!allowedTabs.length) {
    return <Navigate to="/attendance" replace />;
  }

  const isImport = activeTab === "import";
  const isLock = activeTab === "lock";
  const lockTab = allowedTabs.find((t) => t.id === "lock");
  const heroTabs = isImport
    ? []
    : allowedTabs.filter((t) => t.id !== "import");

  return (
    <div className="att-master w-full max-w-full min-w-0 box-border px-4 py-4 md:px-6 md:py-6 lg:px-8">
      <div className="att-master-hero py-4 px-5 md:px-7 md:py-5 mb-5">
        <div className="att-master-hero-accent" />
        <div className="relative z-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                {isImport ? "Import Attendance" : isLock ? "Lock Attendance" : "Attendance Calendar"}
              </h1>
              <p className="text-purple-50/90 text-[13px] mt-1 max-w-2xl leading-relaxed">
                {isImport
                  ? "Excel/CSV upload — quick rows or full templates with validation & audit"
                  : isLock
                    ? "Freeze a verified date range so attendance cannot change while payroll and finance run"
                    : "Mark the month, auto-apply policy days, and lock the period when attendance is final"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {heroTabs.length > 0 && (
                <div
                  className="flex flex-wrap gap-2"
                  role="tablist"
                  aria-label="Attendance calendar sections"
                >
                  {heroTabs.map((tab) => {
                    const Icon = TAB_ICONS[tab.id] || Calendar;
                    const isActive = tab.id === activeTab;
                    const isLockTab = tab.id === "lock";
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        data-color={tab.color}
                        className={`att-master-tab${isActive ? " is-active" : ""}${isLockTab ? " is-lock-tab" : ""}`}
                        onClick={() => selectTab(tab.id)}
                        title={tab.description}
                      >
                        {isLockTab ? <span className="att-lock-pulse-dot" /> : <span className="att-master-tab-dot" data-color={tab.color} />}
                        <Icon className="w-4 h-4 shrink-0 opacity-90" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {isImport && lockTab ? (
                <button
                  type="button"
                  className={`att-master-tab is-lock-tab${isLock ? " is-active" : ""}`}
                  onClick={() => selectTab("lock")}
                  title={lockTab.description}
                >
                  <span className="att-lock-pulse-dot" />
                  <Lock className="w-4 h-4 shrink-0" />
                  <span>{lockTab.label}</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* HELP GUIDE */}
      <div className={`rounded-2xl border overflow-hidden mb-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/60`}>
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center gap-3 px-5 py-3.5 text-left transition hover:bg-blue-100/40"
        >
          <div className="w-8 h-8 rounded-xl text-white flex items-center justify-center shadow-sm shrink-0 bg-blue-600">
            {isLock ? <Lock size={16} /> : <HelpCircle size={16} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-blue-900">
              {isImport
                ? "How to Import Attendance"
                : isLock
                  ? "How to Lock Attendance from this calendar"
                  : "How to use Attendance Calendar"}
            </p>
            <p className="text-[11px] text-blue-700/70">
              {isImport
                ? "Click here for a step-by-step guide on importing attendance via Excel/CSV"
                : isLock
                  ? "Click here to understand when to freeze a period and how to release it"
                  : "Click here for a guide on monthly marking, auto-mark, and locking the month"}
            </p>
          </div>
          <ChevronDown size={16} className={`text-blue-500 transition-transform ${showHelp ? "rotate-180" : ""}`} />
        </button>
        {showHelp && (
          <div className="px-5 pb-5 border-t border-blue-200/60">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
              {isImport ? (
                <>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Quick CSV/Excel Upload</p>
                      <p className="text-[11px] text-slate-500">Use the top section for simple uploads. Your file needs columns: <b>employee_code, work_date, status, work_hours, remarks</b>. Click <b className="text-blue-700">Choose File</b>, select your CSV/Excel, preview the rows, then click <b className="text-blue-700">Upload</b>.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Full Import with Templates</p>
                      <p className="text-[11px] text-slate-500">Scroll down to use the <b>Daily template</b> (employee_code, name, date, check-in/out, status, shift) or <b>Monthly template</b> (grid with day 01–31 columns). Click <b className="text-emerald-700">Download template</b> to get the format.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Fill &amp; Upload</p>
                      <p className="text-[11px] text-slate-500">Fill in the downloaded template with your attendance data. Then drag &amp; drop or click to browse and upload the file. The system validates each row and shows errors before importing.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Import Modes</p>
                      <p className="text-[11px] text-slate-500"><b className="text-blue-700">Replace</b> overwrites existing records for the same employee + date. <b className="text-amber-700">Skip</b> keeps current records and only adds new dates. <b className="text-emerald-700">Merge</b> updates only check-in/out and remarks on existing rows.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">5</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Review &amp; Confirm</p>
                      <p className="text-[11px] text-slate-500">After upload, preview parsed rows and check for errors. Valid rows are highlighted in green, errors in red. Fix any issues in your file and re-upload, or confirm to import the valid rows.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">6</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Batch History</p>
                      <p className="text-[11px] text-slate-500">Every import is logged as a batch. You can view past imports, check how many rows succeeded or failed, and download error reports for each batch.</p>
                    </div>
                  </div>
                </>
              ) : isLock ? (
                <>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Why lock lives in the calendar</p>
                      <p className="text-[11px] text-slate-500">Lock attendance is not a settings page. After you finish marking the month here, freeze that date range so nobody can change days while payroll runs.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Choose the lock level</p>
                      <p className="text-[11px] text-slate-500"><b className="text-sky-700">Attendance</b> blocks day edits. <b className="text-amber-700">Payroll</b> protects figures already used in a pay run. <b className="text-blue-700">Financial</b> is the strictest freeze after books are closed.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Select from / to dates</p>
                      <p className="text-[11px] text-slate-500">Lock the exact month you just verified, for example 2026-08-01 to 2026-08-31. Add a short note so the next admin knows why it was frozen.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Release only for corrections</p>
                      <p className="text-[11px] text-slate-500">Active locks appear on the right. Release a lock if a genuine correction is needed, fix the calendar, then lock the period again.</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Monthly Attendance</p>
                      <p className="text-[11px] text-slate-500">Select a month and employee to view and mark their daily attendance on a calendar grid. Click any day cell to toggle between Present (P), Absent (A), Leave (L), etc. Choose <b>All employees</b> then <b>Fill working days</b> to mark Present for everyone in the organisation.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Apply Calendar Marks</p>
                      <p className="text-[11px] text-slate-500">Auto-mark all working days as <b className="text-emerald-700">Present</b>, plus <b className="text-blue-700">Holiday (H)</b> and <b className="text-amber-700">Week-off (WO)</b> based on your organisation's policy. Saves hours of manual marking.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Daily Records</p>
                      <p className="text-[11px] text-slate-500">For day-by-day attendance with filters, bulk updates, and status changes, use <b className="text-blue-700">Attendance Records</b> from the sidebar or top navigation.</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                    <div>
                      <p className="text-[12.5px] font-bold text-slate-800">Lock Attendance</p>
                      <p className="text-[11px] text-slate-500">Look for the glowing <b className="text-blue-700">Lock Attendance</b> tab in this calendar. After the month is verified, freeze those dates so payroll can run on a stable month. This is not a Settings item.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="att-master-panel">
        <div className="att-master-panel-accent" data-color={tabMeta.color} />
        {activeTab !== "lock" ? (
        <div className="att-master-panel-header">
          {activeTab === "daily" ? (
            <div className="space-y-6">
              {/* Daily Attendance — before Monthly Attendance */}
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50/80 px-3.5 py-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl text-white shadow-sm shrink-0 bg-gradient-to-br from-sky-600 to-blue-600">
                    <ClipboardList className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-slate-900">Daily Attendance</h2>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Want daily attendance? Go to Attendance Records.
                    </p>
                  </div>
                </div>
                <Link
                  to="/attendance/records"
                  className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-white px-3.5 py-2 text-sm font-semibold text-sky-800 hover:bg-sky-100 hover:border-sky-400 transition shadow-sm"
                >
                  Go to Attendance Records
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </Link>
              </div>
{/* 
              {lockTab ? (
                <button
                  type="button"
                  onClick={() => selectTab("lock")}
                  className="att-lock-callout w-full text-left"
                >
                  <span className="att-lock-callout-glow" />
                  <div className="relative z-10 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="att-lock-callout-icon">
                        <Lock className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-extrabold uppercase tracking-wider text-rose-200">
                          Also on this calendar
                        </p>
                        <h2 className="text-base font-bold text-white mt-0.5">Lock Attendance</h2>
                        <p className="text-sm text-white/85 mt-0.5">
                          After this month is verified, freeze the dates so payroll cannot be changed by a late edit.
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-xl bg-white px-3.5 py-2 text-sm font-bold text-rose-700 shadow-sm">
                      <ShieldAlert className="w-4 h-4" />
                      Open lock
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </button>
              ) : null} */}

              {/* Monthly Attendance */}
              <div className="flex items-start gap-3 min-w-0 pt-5 mt-1 border-t border-slate-200">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl text-white shadow-sm shrink-0 bg-gradient-to-br from-teal-600 to-teal-500">
                  <ActiveIcon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold text-slate-900 leading-tight">Monthly Attendance</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Select a month and employee to view and mark that employee&apos;s monthly calendar. Choose <b>All employees</b> and Fill working days to apply Present for the whole organisation.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-xl text-white shadow-sm"
                style={{
                  background:
                    tabMeta.color === "teal"
                      ? "linear-gradient(135deg,#0d9488,#14b8a6)"
                      : tabMeta.color === "indigo"
                        ? "linear-gradient(135deg,#4338ca,#6366f1)"
                        : tabMeta.color === "blue"
                          ? "linear-gradient(135deg,#1d4ed8,#3b82f6)"
                          : tabMeta.color === "purple"
                            ? "linear-gradient(135deg,#6d28d9,#a855f7)"
                            : tabMeta.color === "rose"
                              ? "linear-gradient(135deg,#e11d48,#f43f5e)"
                              : "linear-gradient(135deg,#d97706,#f59e0b)",
                }}
              >
                <ActiveIcon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">{tabMeta.label}</h2>
                <p className="text-sm text-slate-500">{tabMeta.description}</p>
              </div>
            </div>
          )}
        </div>
        ) : null}
        <div className="att-master-panel-body">
          {ActivePanel ? (
            <ActivePanel
              embedded
              key={activeTab}
              {...(activeTab === "daily" && allowedTabs.some((t) => t.id === "apply")
                ? { onNavigateToApply: () => selectTab("apply") }
                : {})}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
